// Coaching in the fight itself: a newcomer (a kid above all) skips the title
// screen's two sentences and the intro card's tip, so the game shows what to
// do at the moment it matters. Time slows as the first opponents of the first
// exam step into range, with a NOW! on their side; the first full Kiai bar is
// pointed out; an exam's new opponent type carries a word on what to do as it
// walks in, till it's first struck; a touch that swipes is told to just tap
// (the half touched strikes, as the finger comes down). The coach only slows
// the clock game.ts runs the sim on, as a Kiai's slow motion does: the sim and
// its judgements never see it, so seeds, timing and scores stay as they are.

import { inReach, type Enemy, type Sim, type Side, type SimEvent } from '@/sim/sim';
import { TUNING as T, type EnemyKind } from '@/sim/tuning';
import { EXAMS } from '@/content/exams';
import type { Mode } from '@/meta/modes';
import type { Save } from '@/meta/save';

/** Save.coach bits: what the player has shown they know. */
export const COACHED = { kiai: 1, tap: 2 } as const;

/** What a match coaches. */
export interface CoachPlan {
  /** The first strikes, slowed down with a NOW! (the first exam, until it's passed). */
  strikes: boolean;
  /** The full Kiai bar, pointed out (until the player has used a Kiai). */
  kiai: boolean;
  /** The opponent type the exam brings, labelled as it first walks in (until it's passed). */
  introduces: EnemyKind | null;
  /** A swipe, answered with "just tap" (until the player has shown they tap). */
  tap: boolean;
}

export const NO_COACH: CoachPlan = { strikes: false, kiai: false, introduces: null, tap: false };

/** A friend's challenge is never coached: its score is held against theirs. */
export function coachFor(mode: Mode, save: Save): CoachPlan {
  if (mode.rival) return NO_COACH;
  const exam = mode.kind === 'exam' ? mode.exam : -1;
  const fresh = exam >= 0 && !save.stars[exam];
  const kind = fresh ? EXAMS[exam]?.introduces : undefined;
  return {
    strikes: fresh && exam === 0,
    kiai: (save.coach & COACHED.kiai) === 0,
    introduces: kind && kind !== 'basic' ? kind : null,
    tap: (save.coach & COACHED.tap) === 0,
  };
}

/** Strikes coached in each try of the first exam. */
const STRIKES = 3;
/** The pace time slows to, and the real seconds it takes to ease there and back. */
export const COACH_PACE = 0.3;
const EASE = 0.15;
/** How far outside reach an opponent slows time, so it's slow by the time it's in. */
const LEAD = 0.25;
/** Real seconds the first full Kiai bar holds time slow. */
const KIAI_SLOW = 2;
/** Sim seconds a new opponent's word rides along, at most. */
const LABEL_T = 8;
/** Real seconds the "just tap" tip shows after a swipe (once a match). */
export const TAP_TIP = 3;
/** Taps in a row, no swipe among them, that show the player knows to tap. */
export const TAPS = 10;

export class Coach {
  /** The pace to run the sim at: 1, or slower while something's coached. */
  pace = 1;
  /** The side to strike now, and the side an opponent is coming in on (0: none). */
  now: Side | 0 = 0;
  near: Side | 0 = 0;
  /** The full Kiai bar to point out. */
  kiaiReady = false;
  /** The new opponent type's first one on the mat, till it's first struck. */
  label: Enemy | null = null;
  /** A swipe just now: the "just tap" tip shows. */
  tapTip = false;
  private strikes: number;
  private kiai: boolean;
  private kiaiSlow = KIAI_SLOW;
  private labelAt = 0;
  private labelled = false;
  private tapLeft = 0;
  private tipped = false;
  private taps = 0;

  constructor(private readonly plan: CoachPlan = NO_COACH) {
    this.strikes = plan.strikes ? STRIKES : 0;
    this.kiai = plan.kiai;
  }

  /** The sim's events as they happen; returns the COACHED bits the player just showed. */
  onEvents(events: readonly SimEvent[], time: number): number {
    let learned = 0;
    for (const ev of events) {
      if (ev.type === 'hit') {
        this.strikes = Math.max(0, this.strikes - 1);
        // Struck, its word has done its job (and leaves the hit's call room)
        if (ev.enemy === this.label) this.label = null;
      } else if (ev.type === 'kiai' && this.kiai) {
        this.kiai = false;
        learned |= COACHED.kiai;
      } else if (
        ev.type === 'spawn' &&
        !this.labelled &&
        !ev.enemy.boss &&
        ev.enemy.kind === this.plan.introduces
      ) {
        this.label = ev.enemy;
        this.labelAt = time;
        this.labelled = true;
      }
    }
    return learned;
  }

  /** A touch in the fight lifted (`swiped`: it moved like a swipe); returns
   *  the COACHED bits the player just showed. Strikes don't wait for this:
   *  the touch struck as it came down. */
  touched(swiped: boolean): number {
    if (!this.plan.tap) return 0;
    if (swiped) {
      this.taps = 0;
      if (!this.tipped) {
        this.tipped = true;
        this.tapLeft = TAP_TIP;
        this.tapTip = true;
      }
      return 0;
    }
    return ++this.taps === TAPS ? COACHED.tap : 0;
  }

  /** Each frame (`dt` real seconds): what shows and how fast time runs. Only
   *  a fight is coached; between rounds time runs at its pace. */
  update(sim: Sim, dt: number, fighting: boolean): void {
    const s = sim.state;
    this.now = 0;
    this.near = 0;
    let slow = false;
    if (fighting && this.strikes > 0) {
      // The nearest opponent coming in (one knocked back is no longer coming)
      let next: Enemy | undefined;
      for (const side of [-1, 1] as Side[]) {
        const e = sim.front(side);
        if (!e || e.state === 'recoil' || e.d > T.reach + LEAD) continue;
        if (!next || e.d < next.d) next = e;
      }
      if (next) {
        slow = true;
        this.near = next.side;
        if (inReach(next)) this.now = next.side;
      }
    }
    if (fighting && this.tapLeft > 0) this.tapLeft -= dt;
    this.tapTip = fighting && this.tapLeft > 0;
    // The Kiai tip waits while "just tap" shows in its place, and its slow
    // motion with it
    this.kiaiReady = fighting && this.kiai && s.kiai >= 1;
    if (this.kiaiReady && !this.tapTip && this.kiaiSlow > 0) {
      this.kiaiSlow -= dt;
      slow = true;
    }
    if (this.label && (this.label.state === 'dead' || s.time - this.labelAt > LABEL_T)) {
      this.label = null;
    }
    if (!fighting) {
      this.pace = 1;
      return;
    }
    // Eased both ways: never a jolt in the opponents' pace
    const step = (dt * (1 - COACH_PACE)) / EASE;
    this.pace = slow ? Math.max(COACH_PACE, this.pace - step) : Math.min(1, this.pace + step);
  }
}
